import axios from "axios";

/**
 * Resolve backend URL dynamically.
 *
 * The .env-baked REACT_APP_BACKEND_URL is fine for preview (where the bundle is built
 * with the preview host), but it gets stamped into the production bundle the same way,
 * leaking "emergent.com" URLs on the connex.is deployment.
 *
 * Fix: if we're running in a browser on a non-emergent custom domain (e.g., connex.is),
 * always talk to that same origin. Backend & frontend share the domain via K8s ingress.
 *
 * Result — works correctly in BOTH envs:
 *   • Preview  (host = enterprise-ledger-10.preview.emergentagent.com) → uses env var
 *   • Prod    (host = connex.is)                                       → uses window.location.origin
 */
function resolveBackendUrl() {
  if (typeof window !== "undefined") {
    const origin = window.location.origin;
    if (origin && !/emergent/i.test(origin)) {
      return origin;
    }
  }
  return process.env.REACT_APP_BACKEND_URL || "";
}

export const BACKEND_URL = resolveBackendUrl();
export const API_BASE = `${BACKEND_URL}/api`;
export const TOKEN_KEY = "connexis_token";
/** A demo session lives in this tab only, so a real sign-in in the same browser is left alone. */
export const DEMO_TOKEN_KEY = "cx_demo_token";
export const authToken = () => sessionStorage.getItem(DEMO_TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);

export const api = axios.create({ baseURL: API_BASE, withCredentials: true });

api.interceptors.request.use((config) => {
  // Send Authorization header alongside the cookie for the transition period —
  // backend prefers the httpOnly cookie but falls back to this header.
  const t = authToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // FastAPI sends a 422 `detail` as an array of objects. Dozens of call sites do
    // `toast.error(err.response.data.detail)`, and handing React an object throws
    // "Objects are not valid as a React child" — which takes the whole page down to the
    // error boundary instead of showing the message. Normalise it to a string once, here.
    const data = err?.response?.data;
    if (data && typeof data === "object" && data.detail != null && typeof data.detail !== "string") {
      data.detail = formatApiError(data.detail);
    }
    if (err?.response?.status === 401 && typeof window !== "undefined") {
      // soft-clear token on 401
      const onAuthPage = window.location.pathname.startsWith("/login");
      if (!onAuthPage) {
        if (sessionStorage.getItem(DEMO_TOKEN_KEY)) sessionStorage.removeItem(DEMO_TOKEN_KEY);
        else localStorage.removeItem(TOKEN_KEY);
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(fieldError).join(" · ") || "That didn't validate.";
  if (detail && typeof detail.msg === "string") return fieldError(detail);
  return String(detail);
}

/** One Pydantic validation error as a sentence a person can act on. */
function fieldError(e) {
  if (!e || typeof e !== "object") return String(e);
  if (typeof e.msg !== "string") return JSON.stringify(e);
  const field = Array.isArray(e.loc) ? e.loc.filter((p) => p !== "body").join(".") : "";
  const name = field ? field.replace(/_/g, " ") : "";
  return name ? `${name}: ${e.msg}` : e.msg;
}
